import { FC, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { useAppSelector } from '../app/hooks'
import { isMarketplaceEnabled } from '../app/featureFlags'
import { Button } from './ui/Button'

/** Site navigation bar with desktop links and mobile drawer. */
export const Navbar: FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const marketplaceEnabled = isMarketplaceEnabled()
  const cartCount = useAppSelector((state) =>
    state.cart.items.reduce((sum, item) => sum + item.quantity, 0),
  )

  return (
    <nav className='w-full bg-brand-smoky/95 shadow-[0_6px_20px_rgba(0,0,0,0.35)] backdrop-blur'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='flex min-h-16 flex-wrap items-center justify-between gap-4 py-2'>
          <div className='flex-1 min-w-[12rem] text-xl font-semibold tracking-wide text-brand-blue leading-snug'>
            <Link to='/' className='brand-title button-link font-display whitespace-normal'>
              Ukraine Metal Festival
            </Link>
          </div>

          {/* Center/Right: Navigation links */}
          <div className='hidden lg:flex ml-auto space-x-6 text-sm uppercase tracking-[0.2em] text-brand-white'>
            {[
              { to: '/', label: 'Home' },
              ...(marketplaceEnabled ? [{ to: '/merch', label: 'Merch & Donations' }] : []),
              { to: '/events', label: 'Events' },
              { to: '/contact', label: 'Contact Us' },
            ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => `nav-link hover:text-brand-gold${isActive ? ' active' : ''}`}
            >
              {item.label}
            </NavLink>
          ))}
          </div>

          {/* Right: Shopping basket */}
          <div className='flex items-center gap-3'>
            <button
              type='button'
              className='hamburger-button flex flex-col gap-2 lg:hidden'
              onClick={() => setIsOpen((value) => !value)}
              aria-label='Toggle menu'
              aria-expanded={isOpen}
              aria-controls='mobile-menu'
            >
              <span className='hamburger-line' />
              <span className='hamburger-line' />
              <span className='hamburger-line' />
            </button>
            {marketplaceEnabled && (
              <NavLink
                to='/cart'
                aria-label='Shopping cart'
                className='nav-link relative text-xl text-brand-azure/80 hover:text-brand-gold'
              >
                🛒
                {cartCount > 0 && (
                  <span className='absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand-gold text-sm text-brand-smoky'>
                    {cartCount}
                  </span>
                )}
              </NavLink>
            )}
          </div>
        </div>
      </div>

      {/* Mobile navigation */}
      {isOpen && (
        <div className='fixed left-0 top-0 h-screen w-screen z-[999] lg:hidden' id='mobile-menu'>
          <div
            className='absolute inset-0 h-full w-full bg-black'
            onClick={() => setIsOpen(false)}
            role='button'
            aria-label='Close menu'
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') {
                setIsOpen(false)
              }
            }}
          />
          <div className='absolute left-0 top-0 h-full w-full bg-brand-smoky p-6 shadow-[12px_0_30px_rgba(0,0,0,0.35)]'>
            <div className='flex items-center justify-between'>
              <span className='text-lg font-semibold text-brand-blue'>Menu</span>
              <button
                type='button'
                className='hamburger-button flex items-center justify-center'
                onClick={() => setIsOpen(false)}
                aria-label='Close menu'
              >
                <span className='hamburger-x'>
                  <span className='hamburger-x-line' />
                  <span className='hamburger-x-line' />
                </span>
              </button>
            </div>
            <div className='mt-6 flex flex-col items-stretch divide-y divide-brand-blue/30 text-base uppercase tracking-[0.2em] text-brand-white'>
              {[
                { to: '/', label: 'Home' },
                ...(marketplaceEnabled ? [{ to: '/merch', label: 'Merch & Donations' }] : []),
                { to: '/events', label: 'Events' },
                { to: '/contact', label: 'Contact Us' },
                ...(marketplaceEnabled ? [{ to: '/cart', label: `Cart (${cartCount})` }] : []),
              ].map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `nav-link w-full py-3 text-center hover:text-brand-gold${isActive ? ' active' : ''}`
                  }
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
